import { Card } from '@nrx/ui';
import { type JSX, useEffect, useRef, useState } from 'react';

import { buildBookingUrl } from '../lib/build-booking-url.js';
import { ZOOM_ORIGIN } from '../lib/zoom.js';

/** Fallback height (px) so the Book button is reachable without inner scroll before/without an adjustSize event. */
const FALLBACK_IFRAME_HEIGHT = 1200;
/**
 * Never shrink below this — guards against a bogus tiny resize value collapsing the frame.
 *
 * 240, not 700. A live NC booking page reports `678px` (measured 2026-09-08), so the old 700 floor
 * REJECTED the real height and left the frame at its 1200px fallback: 522px of slack that Zoom
 * fills by centring its content, which is what put ~261px of whitespace above the calendar. The
 * floor only has to rule out a collapse, and it must stay well under any legitimate render —
 * including Zoom's post-booking confirmation view, which is shorter than the calendar.
 */
const MIN_IFRAME_HEIGHT = 240;
/**
 * An origin or slug problem produces a blank frame with no console error and no network failure —
 * Zoom just never posts back. `onUnresponsive` fires once if this much time passes after mount
 * with no message of any kind seen from {@link ZOOM_ORIGIN}, so callers can surface a fallback.
 */
const UNRESPONSIVE_TIMEOUT_MS = 8_000;

/**
 * Whether a message came from this frame's page, or a frame nested inside it. Every mounted frame
 * hears every Zoom message on the window, so without this one booking reached the `onBooked` of
 * each frame on the page. A `null` source is a synthetic event, which only same-origin script can
 * dispatch.
 */
function isFromFrame(source: MessageEventSource | null, frame: Window | null | undefined): boolean {
  if (source === null) return true;
  for (let w: Window | null = source as Window; w; w = w.parent === w ? null : w.parent) {
    if (w === frame) return true;
  }
  return false;
}

export interface ZoomBookingMember {
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone?: string;
}

export interface ZoomBookedPayload {
  readonly scheduledEventId: string;
  readonly attendeeId: string;
}

/**
 * Embeds the Zoom scheduler and translates its postMessage protocol into two callbacks: auto-height
 * (`adjustSize`) and booking completion (`bookingForm`). Mechanics only — no API calls, no
 * confirmation UI. Callers own what happens after `onBooked` fires.
 *
 * @remarks Renders NOTHING and fires `onUnresponsive` immediately when `bookingUrl` is not an
 * embeddable absolute http(s) URL (see {@link buildBookingUrl}). That reuses the caller's existing
 * escape hatch — the same link affordance the unresponsive-timeout path renders — for a second
 * cause with the same member-visible symptom, "the frame will never work". It also means an
 * operator-authored bad URL degrades this one frame instead of throwing out of render and blanking
 * the entire app, which is what `new URL()` did before.
 */
export function ZoomBookingFrame({
  bookingUrl,
  member,
  title,
  onBooked,
  onUnresponsive,
}: {
  readonly bookingUrl: string;
  readonly member: ZoomBookingMember;
  readonly title: string;
  readonly onBooked: (payload: ZoomBookedPayload) => void;
  /**
   * Fires synchronously on mount if `bookingUrl` is not embeddable, or after
   * {@link UNRESPONSIVE_TIMEOUT_MS} with no message from Zoom otherwise. Because embeddability is
   * re-evaluated per `bookingUrl`, it can fire more than once across a bad → good → bad URL swap.
   */
  readonly onUnresponsive?: () => void;
}): JSX.Element | null {
  const [height, setHeight] = useState(FALLBACK_IFRAME_HEIGHT);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Computed during render, but TOTAL: `null` rather than a thrown TypeError for a non-absolute or
  // non-http(s) value. `src === null` is the whole "cannot be embedded" signal below.
  const src = buildBookingUrl(bookingUrl, { ...member, origin: window.location.origin });
  const embeddable = src !== null;

  // `onBooked`/`onUnresponsive` are commonly passed as fresh inline arrows on every render (every
  // call site in this repo does). If the effect below depended on them directly, a re-render would
  // tear down and reinitialize it — restarting the 8s clock AND resetting `responded` to false even
  // after Zoom already spoke. Refs let the mount-only effect always call the LATEST callback
  // without being one of its dependencies.
  const onBookedRef = useRef(onBooked);
  const onUnresponsiveRef = useRef(onUnresponsive);
  useEffect(() => {
    onBookedRef.current = onBooked;
    onUnresponsiveRef.current = onUnresponsive;
  });

  useEffect(() => {
    if (!embeddable) {
      // No iframe will render, so no message can ever arrive and the 8s window below would only
      // delay the inevitable. Surface the caller's fallback now.
      onUnresponsiveRef.current?.();
      return;
    }

    // Any message from Zoom — not just adjustSize/bookingForm — proves the frame is alive, so it
    // cancels the unresponsive fallback below regardless of what it is. Lives for the component's
    // full lifetime (this effect never re-runs), so a re-render can't reset it back to false.
    let responded = false;

    function onMessage(event: MessageEvent): void {
      if (event.origin !== ZOOM_ORIGIN) return;
      if (!isFromFrame(event.source, iframeRef.current?.contentWindow)) return;
      responded = true;
      const data = event.data as
        | {
            type?: string;
            height?: number | string;
            payload?: { scheduledEventId?: string; attendeeId?: string };
          }
        | null
        | undefined;
      if (data == null) return;

      // Auto-height: grow the frame to fit its content so the Book button is reachable without
      // scrolling inside the iframe.
      //
      // `contentResized` is what Zoom ACTUALLY sends — captured from a live embed on 2026-09-08 as
      // `{ type: 'contentResized', height: '678px' }`, origin `https://scheduler.zoom.us`.
      // `adjustSize` was this repo's own invention: it appeared nowhere but our fixtures, so every
      // test agreed with the code and none with Zoom, and auto-height had never once worked in any
      // consumer. `adjustSize` is kept only because accepting a name we have not observed costs
      // nothing and removing it would be a second unverified belief in the other direction.
      if (data.type === 'contentResized' || data.type === 'adjustSize') {
        const h = typeof data.height === 'string' ? Number.parseFloat(data.height) : data.height;
        if (typeof h === 'number' && Number.isFinite(h) && h >= MIN_IFRAME_HEIGHT) {
          setHeight(h);
        }
        return;
      }

      if (data.type !== 'bookingForm') return;
      const scheduledEventId = data.payload?.scheduledEventId;
      const attendeeId = data.payload?.attendeeId;
      if (scheduledEventId === undefined || attendeeId === undefined) return;
      onBookedRef.current({ scheduledEventId, attendeeId });
    }
    window.addEventListener('message', onMessage);

    // Started once, at mount, regardless of whether `onUnresponsive` was passed on this render —
    // whether to actually CALL it is decided at fire time via the ref, so a prop that arrives or
    // disappears later can't restart (or skip) the window.
    const timer = window.setTimeout(() => {
      if (!responded) onUnresponsiveRef.current?.();
    }, UNRESPONSIVE_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
    };
    // Effectively mount-only: `embeddable` is derived from `bookingUrl` and does not change on a
    // re-render with the same url, so the 8s window is NOT restarted (the Task 6 regression). It is
    // a dependency rather than a closure read so that a caller swapping a bad url for a good one
    // still gets a live frame. See the refs above for why the callbacks aren't dependencies.
  }, [embeddable]);

  // Nothing embeddable to show: the caller was told via `onUnresponsive` and owns the fallback, so
  // rendering an empty Card here would just be a mystery box above it.
  if (src === null) return null;

  // VISUAL PARITY WITH WHAT ffs-signup SHIPPED, which is a live production surface. Extracting this
  // frame out of `booking-step.tsx` silently restyled it: the original wrapper was `.nrx-card`
  // (`border: 1px solid var(--color-card-border)` AND `box-shadow: var(--shadow-card)`) with
  // `rounded-[var(--radius-lg)]` — 24px. `Card elevation="flat"` is `.card` + `.card--flat`, which
  // keeps a border but sets `box-shadow: none`, and `.card`'s own radius is `--nrx-radius-md`, 16px.
  // So the booking card quietly lost its shadow and 8px of corner on the surface members actually
  // use, and no test could see it: jsdom applies no stylesheets.
  //
  // The DEFAULT elevation (`card`, hence no modifier) is the one whose `box-shadow` is
  // `var(--shadow-card)` — the same token `.nrx-card` used. `.card` also sets `border: none`, so the
  // border comes back explicitly here, and the radius is pinned to `--nrx-radius-lg` (24px, equal to
  // ffs-signup's `--radius-lg`). Together those reproduce the original three properties exactly.
  return (
    <Card className="overflow-hidden rounded-[var(--nrx-radius-lg)] border border-[var(--color-card-border)] p-1.5 sm:p-2">
      <iframe
        ref={iframeRef}
        title={title}
        src={src}
        style={{ height: `${height}px` }}
        // 16px, matching ffs-signup's original `rounded-[var(--radius)]` on the iframe itself.
        className="w-full rounded-[var(--nrx-radius-md)] border-0"
      />
    </Card>
  );
}
